/**
 * Loan Summary Card Component
 *
 * Displays critical loan information for the Conditions Portal:
 * - Loan number (prominent)
 * - Borrower name(s)
 * - Property address
 * - Loan amount and program
 * - Loss Payee / Mortgagee clause
 */

import { FileText, MapPin, Users, DollarSign, Building2, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { ConditionsPortalLoanData } from '@/types/conditions-portal';

interface LoanSummaryCardProps {
  loanData: ConditionsPortalLoanData;
  documentsNeededBy?: string | null;
}

/**
 * Format currency
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

export const LoanSummaryCard = ({ loanData, documentsNeededBy }: LoanSummaryCardProps) => {
  // Format borrower name(s)
  const borrowerNames = loanData.coBorrower
    ? `${loanData.borrower.name} & ${loanData.coBorrower.name}`
    : loanData.borrower.name;

  // Format property address
  const propertyAddress = `${loanData.property.address}, ${loanData.property.city}, ${loanData.property.state} ${loanData.property.zip}`;

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Loan Info Card */}
      <Card className="shadow-sm bg-white border-[#183C2E]">
        <CardContent className="p-4 space-y-3">
          {/* Loan Number - Prominent */}
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#183C2E]" />
            <span className="text-sm text-gray-500">Loan #</span>
            <span className="text-lg font-bold text-gray-900">
              {loanData.loanNumber || 'N/A'}
            </span>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {/* Borrower */}
            <div className="flex items-start gap-2">
              <Users className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <span className="text-gray-500 block text-xs">Borrower</span>
                <span className="text-gray-900 font-medium">{borrowerNames}</span>
              </div>
            </div>

            {/* Loan Amount & Program */}
            <div className="flex items-start gap-2">
              <DollarSign className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <span className="text-gray-500 block text-xs">Loan Amount</span>
                <span className="text-gray-900 font-medium">
                  {formatCurrency(loanData.loan.totalLoanAmount)}
                </span>
              </div>
            </div>

            {/* Property - Full Width */}
            <div className="flex items-start gap-2 sm:col-span-2">
              <MapPin className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <span className="text-gray-500 block text-xs">Property</span>
                <span className="text-gray-900 font-medium">{propertyAddress}</span>
              </div>
            </div>
          </div>

          {documentsNeededBy && (
            <div className="flex items-start gap-2 pt-2 border-t border-gray-100">
              <Clock className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
              <div>
                <span className="text-gray-500 block text-xs">Documents Needed By</span>
                <span className="text-red-600 font-semibold text-sm">{documentsNeededBy}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loss Payee / Mortgagee Clause Card */}
      <Card className="shadow-sm bg-white border-[#183C2E]">
        <CardContent className="pt-6">
          <div className="flex items-start gap-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
            <Building2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <span className="text-blue-800 block text-xs font-semibold uppercase tracking-wide">
                Loss Payee / Mortgagee Clause
              </span>
              <div className="text-sm text-gray-900 mt-1 space-y-0.5">
                <p className="font-medium">Lendwise Mortgage, Inc. ISAOA</p>
                <p>21800 Oxnard Street #220</p>
                <p>Woodland Hills, CA 91367</p>
                <p className="text-gray-600">Loan #: {loanData.loanNumber}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoanSummaryCard;
